import { FaLinkedinIn, FaInstagram, FaFacebookF, FaWhatsapp } from "react-icons/fa";
import { Mail, Phone, Calendar } from "lucide-react";
import { trackEmailClick, trackPhoneClick, trackWhatsappClick, trackMeetingLinkClick, SERVICE } from "@/lib/tracking";

const LOGO = "https://customer-assets.emergentagent.com/job_leads-paid-ads/artifacts/c8bafmph_Rekisa%20white.png";
const WHATSAPP_NUMBER = "+27714679642";
const WHATSAPP_URL = "https://wa.me/27714679642";
const MEETING_URL = "https://meetings.hubspot.com/thato6";

function _serviceFromPath() {
  if (typeof window === "undefined") return undefined;
  const p = window.location.pathname;
  if (p.startsWith("/whatsapp-automation")) return SERVICE.whatsapp;
  if (p.startsWith("/sales-system") || p.startsWith("/website-app-creation")) return SERVICE.website;
  if (p.startsWith("/paid-ads-management")) return SERVICE.paid_ads;
  if (p.startsWith("/ecommerce-solutions")) return SERVICE.ecommerce;
  return undefined;
}

const WA_LINKS = [
  { label: "Features", href: "#features", tid: "footer-nav-features" },
  { label: "How It Works", href: "#how-it-works", tid: "footer-nav-how-it-works" },
  { label: "Pricing", href: "#pricing", tid: "footer-nav-pricing" },
  { label: "Demonstration", href: "#demo", tid: "footer-nav-demo" },
  { label: "FAQs", href: "#faqs", tid: "footer-nav-faqs" },
  { label: "Request a Quote", href: "#request-quote", tid: "footer-nav-request-quote" },
];

const ECOM_LINKS = [
  { label: "eCommerce Services", href: "#services", tid: "footer-nav-services" },
  { label: "Online Store Development", href: "#online-store", tid: "footer-nav-online-store" },
  { label: "eCommerce Strategy", href: "#strategy", tid: "footer-nav-strategy" },
  { label: "Marketplace Enablement", href: "#marketplaces", tid: "footer-nav-marketplaces" },
  { label: "How It Works", href: "#how-it-works", tid: "footer-nav-how-it-works" },
  { label: "FAQs", href: "#faqs", tid: "footer-nav-faqs" },
  { label: "Get My eCommerce Plan", href: "#request-quote", tid: "footer-nav-request-quote" },
  { label: "Rekisa main website", href: "https://rekisa.co.za/services/digital-commerce-enablement", tid: "footer-nav-main-site" },
];

const SALES_LINKS = [
  { label: "How It Works", href: "/sales-system#how-it-works", tid: "footer-nav-how-it-works" },
  { label: "Solutions", href: "/sales-system#solutions", tid: "footer-nav-solutions" },
  { label: "Features", href: "/sales-system#features", tid: "footer-nav-features" },
  { label: "Who It's For", href: "/sales-system#use-cases", tid: "footer-nav-use-cases" },
  { label: "Pricing", href: "/sales-system#pricing", tid: "footer-nav-pricing" },
  { label: "FAQs", href: "/sales-system#faqs", tid: "footer-nav-faqs" },
  { label: "Get a Plan", href: "/sales-system#request-plan", tid: "footer-nav-request-plan" },
];

export default function Footer() {
  const service = _serviceFromPath();
  const isWa = service === SERVICE.whatsapp;
  const onSales = service === SERVICE.website;
  const isEcom = service === SERVICE.ecommerce;
  const links = isEcom ? ECOM_LINKS : isWa ? WA_LINKS : SALES_LINKS.map((l) => (onSales ? { ...l, href: l.href.replace("/sales-system", "") } : l));
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-zinc-950 text-zinc-300 py-16 sm:py-20"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 h-40 w-40 rounded-full bg-[#4285f4]/20 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-40 w-40 rounded-full bg-[#ea4335]/20 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1.5fr_0.8fr_1fr_0.8fr] gap-12">
          <div>
            <img
              src={LOGO}
              alt="Rekisa"
              width="192" height="48"
            className="h-12 w-auto object-contain"
              loading="lazy"
            />
            <p data-testid="footer-blurb" className="mt-5 text-zinc-400 max-w-md leading-relaxed text-[15px]">
              {isEcom ? (
                "Rekisa eCommerce Solutions helps South African businesses plan, build and grow online — from strategy and online stores to marketplace enablement on Amazon, Takealot and Makro."
              ) : isWa ? (
                "Rekisa sets up WhatsApp automation for South African businesses — automated replies, lead capture, qualification and human handover. Implemented by Rekisa. Powered by Chatfuel."
              ) : (<>
              Rekisa creates websites, landing pages and digital sales journeys that help small
              businesses capture enquiries, follow up consistently and manage opportunities from
              first contact to sale.
              </>)}
            </p>
          </div>

          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 font-mono mb-4 font-bold">Explore</div>
            <ul className="space-y-3 text-zinc-300 text-sm">
              {links.map((l) => (
                <li key={l.label}>
                  <a href={l.href} data-testid={l.tid} className="hover:text-white transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 font-mono mb-4 font-bold">Contact</div>
            <ul className="space-y-3 text-zinc-300 text-sm">
              <li>
                <a
                  href="mailto:thato@rekisa.co.za"
                  data-testid="footer-email"
                  onClick={() => trackEmailClick({ service, emailAddress: "thato@rekisa.co.za", location: "footer" })}
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Mail className="h-4 w-4 text-[#4285f4]" /> thato@rekisa.co.za
                </a>
              </li>
              <li>
                <a
                  href="tel:+27714679642"
                  data-testid="footer-phone"
                  onClick={() => trackPhoneClick({ service, phoneNumber: "+27714679642", location: "footer" })}
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Phone className="h-4 w-4 text-[#34a853]" /> +27 71 467 9642
                </a>
              </li>
              <li>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="footer-whatsapp"
                  onClick={() => trackWhatsappClick({ service, whatsappNumber: WHATSAPP_NUMBER, location: "footer" })}
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <FaWhatsapp className="h-4 w-4 text-[#25d366]" /> Chat on WhatsApp
                </a>
              </li>
              <li>
                <a
                  href={MEETING_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="footer-book-a-call"
                  onClick={() => trackMeetingLinkClick({ service, linkUrl: MEETING_URL })}
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Calendar className="h-4 w-4 text-[#fbbc04]" /> Book a 20-min call
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 font-mono mb-4 font-bold">Follow</div>
            <div className="flex items-center gap-3">
              <a
                href="https://za.linkedin.com/company/rekisa-sales-marketing-solutions"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="footer-linkedin"
                aria-label="Rekisa on LinkedIn"
                className="h-10 w-10 rounded-full border border-zinc-800 hover:border-[#0a66c2] hover:text-[#0a66c2] text-zinc-400 flex items-center justify-center transition-colors"
              >
                <FaLinkedinIn />
              </a>
              <a
                href="https://www.instagram.com/rekisa_ecommerce/?hl=en"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="footer-instagram"
                aria-label="Rekisa on Instagram"
                className="h-10 w-10 rounded-full border border-zinc-800 hover:border-[#e1306c] hover:text-[#e1306c] text-zinc-400 flex items-center justify-center transition-colors"
              >
                <FaInstagram />
              </a>
              <a
                href="https://www.facebook.com/Getrekisa"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="footer-facebook"
                aria-label="Rekisa on Facebook"
                className="h-10 w-10 rounded-full border border-zinc-800 hover:border-[#1877f2] hover:text-[#1877f2] text-zinc-400 flex items-center justify-center transition-colors"
              >
                <FaFacebookF />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-zinc-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-zinc-400">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span>© {new Date().getFullYear()} Rekisa. All rights reserved.</span>
            <a href="/privacy-policy" data-testid="footer-privacy" className="hover:text-zinc-300 transition-colors">Privacy Policy</a>
            <a href="/terms" data-testid="footer-terms" className="hover:text-zinc-300 transition-colors">Terms &amp; Conditions</a>
            <a href="mailto:thato@rekisa.co.za" data-testid="footer-contact" className="hover:text-zinc-300 transition-colors">Contact</a>
          </div>
          <div className="font-mono uppercase tracking-[0.18em]">Rekisa · South Africa</div>
        </div>
      </div>
    </footer>
  );
}
